/**
 * R17 Task B (architect I3 — DRY): shared tier bucketing for manual checks.
 *
 * R16's ManualChecksSalesmanView.tsx inline-bucketed checks by payout percentage
 * with a ±0.05pp tolerance so "about 20%" entries didn't silently fall into
 * Misc. Task B needs the same logic on the User Report screen — extracted here
 * so both consumers use one source of truth (and the same tolerance).
 *
 * The blade partial (_manual_checks_section.blade.php) uses the same tolerance
 * so PDF + UI agree.
 */

export interface BucketableCheck {
  payout_percentage: number | string;
}

export interface TierBuckets<T> {
  tier20: T[];
  tier10: T[];
  misc: T[];
}

/**
 * Bucket a list of manual checks by their effective payout percentage.
 *
 * A check whose |payout - 20| < tolerance goes to tier20; |payout - 10| <
 * tolerance to tier10; everything else to misc.
 *
 * @param checks   Any object exposing a numeric payout_percentage
 * @param tolerance Absolute pp difference tolerated (default 0.05)
 * @returns        Three arrays keyed by tier
 */
export function bucketChecksByTier<T extends BucketableCheck>(
  checks: readonly T[],
  tolerance: number = 0.05
): TierBuckets<T> {
  const tier20: T[] = [];
  const tier10: T[] = [];
  const misc: T[] = [];
  for (const c of checks) {
    const pct = Number(c.payout_percentage);
    if (!Number.isFinite(pct)) {
      misc.push(c);
      continue;
    }
    if (Math.abs(pct - 20) < tolerance) tier20.push(c);
    else if (Math.abs(pct - 10) < tolerance) tier10.push(c);
    else misc.push(c);
  }
  return { tier20, tier10, misc };
}
